import React, { useEffect, useState } from "react";
import { YStack, XStack, Text, Button, View, Circle, Spinner, ScrollView, Image } from "tamagui";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { colors } from "@styles/colors";
import { formatGasTitle } from "@utils/func/gasTitle";
import TextSpaceBetween from "@components/layout/TextSpaceBetween";
import { RiMapPinLine, RiTimeLine, RiPhoneLine, RiCoinsLine, RiHourglassFill, RiCoupon3Line, RiStore2Line, RiEditLine } from "react-icons/ri";
import { getGasImageByKg } from "@styles/images/quickorder/gasImages";
import { getOrderDetails } from "./API/handleOrderAPI";
import { useUser } from "@utils/store/user/useUser";
import { buildXContextHeader } from "@utils/api/headers";
import { Order as APIOrder, OrderListStatusEnum } from "./type/orderList";
import { isOrderAmountHidden } from "@utils/func/orderAmountVisibility";
import { getRefundHeaderBadge, hasRefundOrders } from "@utils/func/refundDisplay";
import { RefundCard } from "@components/display/RefundCard";
import { amountDeltaText, hasModifications, modificationLabel } from "@utils/func/orderModifications";
import { format } from "date-fns";
import toast from "react-hot-toast";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { handleAPIGetOrderPaymentStatus, type OrderPaymentStatus } from "../Account/API/handlePaymentAPI";
import { shouldFetchPayStatus } from "@utils/applePay";
import { needsPaymentPin, shouldShowPaymentPin } from "@utils/paymentPin";
import * as basicStyle from "@styles/index";
import { useSupplierContact } from "./useSupplierContact";
import { toTelHref } from "@utils/func/supplierIdentity";
import { isGasSkinV2, SkinCard, v2 } from "@screen/Home/pages/GasShop/skin";

const OrderNotFoundImg = require("@styles/images/order_not_found.png") as string;

type Status = "pending" | "waiting_for_pickup" | "delivering" | "completed" | "canceled";

const STS: Record<Status, { l: string; c: string; bg: string; bar: string; icon: string }> = {
  pending: { l: "待確認", c: colors.statusPending, bg: colors.bgAmber, bar: colors.statusPending, icon: "!" },
  waiting_for_pickup: { l: "待接單", c: colors.statusWaiting, bg: colors.primaryActive, bar: colors.statusWaiting, icon: "✓" },
  delivering: { l: "配送中", c: colors.statusDelivering, bg: colors.bgPurple, bar: colors.statusDelivering, icon: "➝" },
  completed: { l: "已完成", c: colors.statusCompleted, bg: colors.bgGreen, bar: colors.statusCompleted, icon: "✓" },
  canceled: { l: "已取消", c: colors.danger, bg: colors.bgRed, bar: colors.danger, icon: "✖" },
};

/**
 * 換皮轉接層（與瓦斯訂購的 GasCard / GasLabel 同一套做法）。
 * V2：扁平白卡（圓角 24、淺陰影）＋灰階區塊標題；V1 輸出與改動前逐字相同，`?skin=v1` 可完整切回。
 */
const DCard = ({ children, space }: { children: React.ReactNode; space?: string }) =>
  isGasSkinV2() ? (
    <SkinCard>
      <YStack gap={14}>{children}</YStack>
    </SkinCard>
  ) : (
    <YStack bg={colors.light} br={12} p="$4" space={space} shadowColor="rgba(0,0,0,0.05)" shadowRadius={5}>
      {children}
    </YStack>
  );

/**
 * 區塊標題（「配送資訊」「訂購項目」）。
 * V2 刻意不用 SkinSectionLabel —— 那個樣式（13.5px 灰色粗體）在這頁被 InfoRow 的欄位標題用掉了，
 * 兩者同款會讓一張卡出現八行等重的標題，分不出段落與欄位。這裡改用深色 15px，明確高一階。
 */
const DTitle = ({ label }: { label: string }) => (
  <Text fontSize={15} fow="700" color={isGasSkinV2() ? v2.title : undefined}>
    {label}
  </Text>
);

/**
 * 資訊列（配送資訊、瓦斯行）。
 * V1：左邊一顆藍底圓形圖示 + 右邊上灰標下值。
 * V2：拿掉圖示色塊，改成與結帳頁一致的「灰色區塊標題在上、值在下」——同一份資料在兩頁
 * 長得一樣，客戶不用重新學版面；圖示色塊是 V1 的語彙，V2 的白卡不用它。
 */
const InfoRow = ({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) =>
  isGasSkinV2() ? (
    // 欄位標題壓成 12px 一般字重的淡灰：它只是替下面那行值命名，不該與區塊標題同重量。
    // 標題與值貼近（gap 3），成對的兩行才會被讀成一組，而不是八行平均散開。
    <YStack gap={3}>
      <Text fontSize={12} lineHeight={15} color={v2.sub}>
        {label}
      </Text>
      {children}
    </YStack>
  ) : (
    <XStack gap="$3">
      <Circle size={32} bg={colors.primaryActive} ai="center" jc="center" flexShrink={0}>
        {icon}
      </Circle>
      <YStack f={1} minWidth={0}>
        <Text fontSize={12} color={colors.gray}>
          {label}
        </Text>
        {children}
      </YStack>
    </XStack>
  );

/**
 * 付款 PIN 碼卡：電子付款訂單在配送員到場收款前，客戶要把這 4 碼唸給配送員核對。
 * 數字放大、逐格顯示，讓客戶隔著門也念得出來；前導零保留（"0042" 是合法 PIN）。
 * V1／V2 共用結構，只換色票：V2 用淡藍格子貼白卡，V1 沿用 primaryActive 色塊。
 */
const PaymentPinCard = ({ pin }: { pin: string }) => {
  const skinV2 = isGasSkinV2();
  const cellBg = skinV2 ? v2.primarySoft : colors.primaryActive;
  const cellBorder = skinV2 ? v2.primarySoftBorder : "transparent";
  const digitColor = skinV2 ? v2.primary : colors.primary;
  return (
    <DCard space="$3">
      <XStack ai="center" jc="space-between" gap="$3">
        <YStack f={1} minWidth={0} gap={4}>
          <Text fontSize={15} fow="700" color={skinV2 ? v2.title : undefined}>
            付款 PIN 碼
          </Text>
          <Text fontSize={12} lineHeight={17} color={skinV2 ? v2.sub : colors.gray}>
            配送員到府收款時，請把這組號碼唸給配送員核對，核對後才會進行扣款。
          </Text>
        </YStack>
        <XStack gap={6} flexShrink={0} aria-label={`付款 PIN 碼 ${pin.split("").join(" ")}`}>
          {pin.split("").map((d, i) => (
            <View
              key={`pin-${i}`}
              width={34}
              height={44}
              br={10}
              bg={cellBg}
              borderWidth={1}
              borderColor={cellBorder}
              ai="center"
              jc="center">
              <Text fontSize={22} fow="800" color={digitColor} style={{ fontVariantNumeric: "tabular-nums" }}>
                {d}
              </Text>
            </View>
          ))}
        </XStack>
      </XStack>
    </DCard>
  );
};

/**
 * 訂單修改紀錄卡。
 *
 * 瓦斯行或配送員在後台改了單，客戶原本完全不會知道——金額變了、品項換了都只有內部看得到。
 * 這張卡把「被改過什麼、什麼時候、錢差多少」攤在訂單詳情，客戶才有機會發現不對並反映。
 * 內容來自 app-api 的 modifications（已濾掉操作者與後台欄位名）。
 */
const ModificationCard = ({ list }: { list: NonNullable<APIOrder["modifications"]> }) => {
  const skinV2 = isGasSkinV2();
  return (
    <DCard space="$3">
      <XStack ai="center" gap={8}>
        <Circle size={22} bg={colors.bgAmber} ai="center" jc="center">
          <RiEditLine size={12} color={colors.amberDark} />
        </Circle>
        <Text fontSize={15} fow="700" color={skinV2 ? v2.title : undefined}>
          訂單修改紀錄
        </Text>
      </XStack>
      <Text fontSize={12} lineHeight={17} color={skinV2 ? v2.sub : colors.gray}>
        這張訂單成立後有被調整過。若與你當初訂的不同，請聯繫瓦斯行確認。
      </Text>
      <YStack gap={10}>
        {list.map((m, idx) => {
          const delta = amountDeltaText(m.amount_delta);
          return (
            <XStack key={`mod-${idx}`} gap={10} ai="flex-start">
              <View w={6} h={6} br={3} bg={colors.amber} mt={6} flexShrink={0} />
              <YStack f={1} minWidth={0} gap={2}>
                <XStack ai="baseline" jc="space-between" gap="$2">
                  <Text fontSize={14} fow="600" f={1} flexShrink={1}>
                    {modificationLabel(m.kind)}
                  </Text>
                  {delta && (
                    <Text fontSize={14} fow="800" color={m.amount_delta! > 0 ? colors.danger : colors.statusCompleted}>
                      {delta}
                    </Text>
                  )}
                </XStack>
                <Text fontSize={12} color={skinV2 ? v2.sub : colors.gray200}>
                  {(() => {
                    try {
                      return format(new Date(m.at), "yyyy/MM/dd HH:mm");
                    } catch {
                      return "";
                    }
                  })()}
                </Text>
              </YStack>
            </XStack>
          );
        })}
      </YStack>
    </DCard>
  );
};

const mapAPIStatusToUI = (status: OrderListStatusEnum): Status => {
  switch (status) {
    case OrderListStatusEnum.pending:
      return "pending";
    case OrderListStatusEnum.waiting_for_pickup:
      return "waiting_for_pickup";
    case OrderListStatusEnum.delivering:
      return "delivering";
    case OrderListStatusEnum.completed:
      return "completed";
    case OrderListStatusEnum.canceled:
      return "canceled";
    default:
      return "pending";
  }
};

const StatusBadge = ({ status }: { status: Status }) => {
  const s = STS[status];
  return (
    <XStack ai="center" gap={5} bg={s.bg} px={isGasSkinV2() ? 10 : "$3"} py={isGasSkinV2() ? 4 : "$1"} br={20} borderWidth={isGasSkinV2() ? 0 : 1} borderColor={`${s.bar}40`}>
      <View w={6} h={6} br={3} bg={s.bar} />
      <Text fontSize={12} fow="700" color={s.c}>
        {s.l}
      </Text>
    </XStack>
  );
};

/** 有退款時頁首徽章改顯示退款中／已退款（配色沿用 amber） */
const RefundStatusBadge = ({ label, done }: { label: string; done: boolean }) => (
  <XStack
    ai="center"
    gap={5}
    bg={done ? "#F5EBDC" : colors.bgAmber}
    px={isGasSkinV2() ? 10 : "$3"}
    py={isGasSkinV2() ? 4 : "$1"}
    br={20}
    borderWidth={isGasSkinV2() ? 0 : 1}
    borderColor={done ? "#B4530940" : `${colors.amber}55`}>
    <View w={6} h={6} br={3} bg={done ? "#B45309" : colors.amber} />
    <Text fontSize={12} fow="700" color={done ? colors.amberDark : "#B45309"}>
      {label}
    </Text>
  </XStack>
);

const StatusBar = ({ status }: { status: Status }) => {
  const skinV2 = isGasSkinV2();
  const steps: Status[] = ["pending", "waiting_for_pickup", "delivering", "completed"];
  const labels = ["待確認", "待接單", "配送中", "已完成"];
  const idx = steps.indexOf(status);
  const lastIdx = steps.length - 1;
  /**
   * V2：整條流程只用一個顏色。
   * 原本每一步各自帶狀態色（琥珀／藍／紫／綠），四個色相擠在一列、連接線還有藍有紫，
   * 看起來像四個不相干的標籤而不是一條進度。目前是什麼狀態，上方的狀態小標已經講了；
   * 這條的工作只有「走到哪」，所以走過＝主藍、未到＝淺灰，當前那步加圈強調。
   */
  const doneColor = colors.primary;
  const restColor = skinV2 ? "#E3EDF8" : colors.gray100;
  return (
    <XStack ai="center" w="100%" jc="space-between">
      {steps.map((st, i) => {
        const done = i <= idx;
        const active = i === idx;
        const s = STS[st];
        const circleBg = skinV2 ? (done ? doneColor : restColor) : done ? s.bar : colors.gray100;
        const glyphColor = skinV2 ? (done ? colors.light : v2.sub) : done ? colors.light : colors.gray200;
        return (
          <React.Fragment key={st}>
            <YStack ai="center" gap="$1" f={1}>
              <Circle
                size={28}
                bg={circleBg}
                borderWidth={skinV2 && active ? 3 : 0}
                borderColor="rgba(33,150,243,0.22)"
                shadowColor={!skinV2 && active ? s.bar : "transparent"}
                shadowRadius={!skinV2 && active ? 8 : 0}
                ai="center"
                jc="center">
                {/* 已走過的步驟一律打勾（含「待確認」的沙漏）——沙漏留在已完成的步驟上會像還在等 */}
                {st === "pending" && !(skinV2 && i < idx) ? (
                  <RiHourglassFill size={14} color={glyphColor} />
                ) : (
                  <Text fontSize={14} color={glyphColor} fow="700">
                    {skinV2 && i < idx ? "✓" : s.icon}
                  </Text>
                )}
              </Circle>
              <Text
                fontSize={10}
                color={skinV2 ? (done ? v2.title : v2.sub) : done ? s.bar : colors.gray200}
                fow={active ? "700" : "400"}
                whiteSpace="nowrap">
                {labels[i]}
              </Text>
            </YStack>
            {i < lastIdx && (
              <View f={1} h={2} bg={skinV2 ? (i < idx ? doneColor : restColor) : i < idx ? STS[steps[i + 1]].bar : colors.gray100} mt={-14} />
            )}
          </React.Fragment>
        );
      })}
    </XStack>
  );
};

const OrderDetail = () => {
  const skinV2 = isGasSkinV2();
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { chooseIdentity } = useUser();
  const { cusJwtToken } = useCusAuth();
  // 可由導頁端（例如帳戶訂單紀錄頁）以 router state 指定 X-Context；未帶則沿用目前身分。
  const stateXContext = (location.state as { xContext?: string } | null)?.xContext;
  // 簡訊邀請落地：訂單可能尚未同步到查詢端。導頁端帶 pending 旗標，或以哨兵 id "pending"
  // 表示「尚無訂單」，此時顯示「訂單建立中」空狀態，而非一般查詢失敗彈回列表。
  const isPendingRoute = orderId === "pending";
  const cameFromPending = (location.state as { pending?: boolean } | null)?.pending === true;
  const [order, setOrder] = useState<APIOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [pendingView, setPendingView] = useState(false);
  // 線上付款狀態（快照為信用卡的訂單才查；GET /payments/order/:id）
  const [payStatus, setPayStatus] = useState<OrderPaymentStatus | null>(null);
  // 付款 PIN 碼（電子付款的在場憑證）：與付款狀態同一支 API 帶回，但顯示條件獨立——
  // LINE Pay 單也有 PIN，卻不走「前往付款」那套狀態徽章，所以分開存。
  const [paymentPin, setPaymentPin] = useState<string | null>(null);
  // 瓦斯行聯絡資料（統編／電話）：訂單 API 未帶，依 supplier_id 由 bootstrap 補齊
  const supplierContact = useSupplierContact(order?.supplier_id ?? order?.supplier?.supplier_id, stateXContext);

  const fetchDetail = React.useCallback(async () => {
      // 哨兵 id：尚無真正訂單，直接進「訂單建立中」空狀態（無可查詢對象）
      if (isPendingRoute) {
        setPendingView(true);
        setLoading(false);
        return;
      }
      if (!orderId || !cusJwtToken) return;
      setLoading(true);
      setPendingView(false);
      const res = await getOrderDetails({
        orderId,
        jwtToken: cusJwtToken,
        // 優先用導頁端指定的 X-Context；否則 chooseIdentity（null=「全部」分頁不送 X-Context）
        xContext: stateXContext ?? buildXContextHeader(chooseIdentity),
      });

      if (res.state === "success") {
        setOrder(res.data.data);
        // 快照為信用卡或 Apple Pay → 查線上付款狀態（已付/未付；未付且可付時顯示「前往付款」）
        // 電子付款（含 LINE Pay）→ 同一支 API 另帶付款 PIN 碼，配送員到場時客戶唸給配送員核對
        const paymentMethod = res.data.data?.payer_snapshot_json?.payment_method;
        const wantPayStatus = shouldFetchPayStatus(paymentMethod);
        const wantPaymentPin = needsPaymentPin(paymentMethod);
        if ((wantPayStatus || wantPaymentPin) && orderId) {
          handleAPIGetOrderPaymentStatus(orderId, cusJwtToken).then((ps) => {
            if (ps.state !== "success") return;
            if (wantPayStatus) setPayStatus(ps.data);
            setPaymentPin(wantPaymentPin ? (ps.data.payment_pin ?? null) : null);
          });
        }
        // 將人類可讀訂單編號（order_list.order_id）與瓦斯行名稱回填到 router state，
        // 讓 PageWrapper 的標頭即使是直接開網址／重新整理也顯示正確編號與瓦斯行
        // （而非 platform_order_id UUID；瓦斯行在「全部」分頁 chooseIdentity 為 null 時也不會消失）。
        const navState = (location.state as { displayId?: string; supplierName?: string | null } | null) ?? null;
        const displayId = res.data.data.supplier_order_snapshot_json?.order_list?.order_id;
        const supplierName = res.data.data.supplier?.supplier_name ?? null;
        const nextDisplayId = displayId ?? navState?.displayId;
        const nextSupplierName = navState?.supplierName ?? supplierName;
        const changed = nextDisplayId !== navState?.displayId || nextSupplierName !== navState?.supplierName;
        if (changed) {
          navigate(location.pathname, {
            replace: true,
            state: { ...(navState as Record<string, unknown> | null), displayId: nextDisplayId, supplierName: nextSupplierName },
          });
        }
      } else if (cameFromPending) {
        // 簡訊邀請導入、但訂單尚未同步到查詢端 → 顯示「訂單建立中」而非彈回列表
        setPendingView(true);
      } else {
        // 一般查詢失敗維持原行為
        toast.error("取得訂單詳情失敗", { id: "fetch-order-detail-error" });
        navigate("/orders");
      }
      setLoading(false);
  }, [orderId, chooseIdentity?.context_id, cusJwtToken, stateXContext, isPendingRoute, cameFromPending, location.pathname, navigate]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  if (loading) {
    return (
      <YStack ai="center" jc="center" py="$20">
        <Spinner size="large" color={colors.primary} />
      </YStack>
    );
  }

  // 「訂單建立中」空狀態：由瓦斯行建立訂單完成前的過場（沿用 DraftNotFoundView 版型）。
  // Header 由 PageWrapper 提供（標題「訂單」），此處僅渲染插圖 + 文案 + 兩顆按鈕。
  if (pendingView) {
    return (
      <View flex={1} w="100%" bg="#FFF" br={12} px="$7" py="$8" justifyContent="center">
        <YStack alignItems="center" w="100%" gap="$4">
          <View w="100%" h={240} alignItems="center" justifyContent="center">
            <Image source={{ uri: OrderNotFoundImg }} alt="訂單建立中" w="100%" h="100%" resizeMode="contain" />
          </View>
          <YStack gap="$2" alignItems="center">
            <Text fontSize={20} fontWeight="800" color={colors.dakerBlue}>
              訂單建立中
            </Text>
            <Text fontSize={14} color={colors.gray} ta="center" maxWidth={320} lineHeight={20}>
              您的訂單正在由瓦斯行建立，完成後即可在此查看詳情
            </Text>
          </YStack>
          <YStack w="100%" gap="$3" mt="$4">
            <Button
              {...basicStyle.ShareBlueButton}
              className="primary-bg-gradient"
              color="#FFF"
              size="$5"
              w="100%"
              onPress={() => {
                setLoading(true);
                void fetchDetail();
              }}>
              重新整理
            </Button>
            <Button {...basicStyle.ShareWhiteButton} size="$5" w="100%" onPress={() => navigate("/orders")}>
              回訂單列表
            </Button>
          </YStack>
        </YStack>
      </View>
    );
  }

  if (!order) return null;

  const uiStatus = mapAPIStatusToUI((order?.order_status ?? "pending") as OrderListStatusEnum);
  // 該瓦斯行開啟隱藏金額設定、且訂單為待確認：隱藏訂購項目單價與整個「訂單摘要」，待瓦斯行確認後才顯示
  const hideAmount = isOrderAmountHidden(order?.hide_amount_on_pending, uiStatus);
  const stepMsg = {
    pending: "已收到您的訂單！正等待瓦斯行接單確認",
    waiting_for_pickup: "瓦斯行已接單！正為您安排配送員",
    delivering: "配送員已出發！正火速前往您的外送地址",
    completed: "訂單已順利送達！感謝您的訂購",
    canceled: "訂單已取消",
  }[uiStatus];

  // AW（瓦斯行後台）設定、隨訂單處理陸續同步進來的費用項目；有資料才顯示
  const snapshot = order.supplier_order_snapshot_json;
  const usageFees = snapshot?.usage_fees ?? [];
  const usageFeeTotal = usageFees.reduce((sum, u) => sum + (u.money || 0), 0);
  const mortgages = snapshot?.mortgages ?? [];
  const refunds = snapshot?.refunds ?? [];
  const orderItems = snapshot?.items ?? [];
  const itemsSubtotal = orderItems.reduce((sum, it) => sum + Number(it.subtotal || 0), 0);
  const cylinders = snapshot?.cylinders ?? [];
  // 鋼瓶買賣：客戶購入(+) / 客戶賣出(−)
  const cylinderNet = cylinders.reduce((s, c) => s + (c.customer_action_type === "客戶賣出" ? -Number(c.subtotal || 0) : Number(c.subtotal || 0)), 0);
  // 押退桶：押桶/借桶/還桶/寄桶 一律相加（對齊 total 公式）
  const mortgageTotal = mortgages.reduce((s, m) => s + Number(m.subtotal || 0), 0);
  // 退氣/存氣、折扣/瓦斯折扣：折抵(−)
  const refundTotal = refunds.reduce((s, r) => s + Number(r.amount || 0), 0);
  const discountTotal = Number(snapshot?.order_list?.discount || 0) + Number(snapshot?.order_list?.gas_discount || 0);
  // 樓層費(+) / 樓層費折抵(−) / 優惠券折抵(−)：對齊 recalculate_order_total_price 公式
  const floorFee = Number(snapshot?.order_list?.floor_fee || 0);
  const floorFeeDiscount = Number(snapshot?.order_list?.floor_fee_discount_amount || 0);
  const couponDiscount = Number(snapshot?.order_list?.coupon_supplier_discount_amount || 0);
  // 優惠券折抵「直接折在瓦斯品項上」（與訂購頁／結帳頁一致，不單列一行）：
  // 按各瓦斯品項金額比例攤折扣，最後一筆瓦斯吸收四捨五入餘數。
  const gasItemDiscount = new Map<number, number>();
  if (couponDiscount > 0) {
    const gasIdxs = orderItems.map((it, i) => (it.kind === "gas" ? i : -1)).filter((i) => i >= 0);
    const gasSum = gasIdxs.reduce((s, i) => s + Number(orderItems[i].subtotal || 0), 0);
    if (gasSum > 0) {
      // 不信任外部資料：折扣上限為瓦斯總額，避免折扣 > 瓦斯小計時某筆顯示負數（與 buildGasCouponDiscountMap 一致）
      const effective = Math.min(couponDiscount, gasSum);
      let remaining = effective;
      gasIdxs.forEach((i, n) => {
        const d = n === gasIdxs.length - 1 ? remaining : Math.round((effective * Number(orderItems[i].subtotal || 0)) / gasSum);
        remaining -= d;
        gasItemDiscount.set(i, d);
      });
    }
  }
  // 各行加總（= total_price_cached 權威公式：品項 + 鋼瓶買賣 + 押退桶 + 使用費 + 樓層費 − 退氣 − 折扣 − 樓層費折抵 − 優惠券）
  const computedTotal =
    itemsSubtotal + cylinderNet + mortgageTotal + usageFeeTotal + floorFee - refundTotal - discountTotal - floorFeeDiscount - couponDiscount;
  // 應付金額優先採權威 total_price_cached（保證頭線正確）；缺值時退回逐行加總
  const totalPriceCached = snapshot?.order_list?.total_price_cached;
  const payableTotal = totalPriceCached != null ? Number(totalPriceCached) : computedTotal;
  // 金錢退款（refund_order）：app-api 詳情頂層欄位（join 表），不讀 snapshot。
  // 與上方 refunds（退氣／存氣公斤數）是兩回事；後者已參與 computedTotal。
  const refundOrders = order.refund_orders;
  const showRefundUi = hasRefundOrders(refundOrders);
  const refundHeader = getRefundHeaderBadge(refundOrders);

  // 會計把線上信用卡單改以現金收款（cash_fallback）：這張單不會扣卡，付款方式一律顯示「現金」，
  // 不再顯示原本下訂時選的信用卡通路（否則客戶看到「信用卡 •••• 4242」會以為被刷卡）。
  // 上方「支付方式」列與下方訂單摘要「付款方式」共用這個值，避免兩處說法不一致。
  const isCashFallback = payStatus?.payment_status === "cash_fallback";
  const paymentMethodLabel = isCashFallback ? "現金" : order.payer_snapshot_json?.label || "-";

  // 實付金額：payups 為瓦斯行端同步進來的收款明細；訂單完成後加總呈現實際支付金額。
  const payups = snapshot?.payups ?? [];
  const paidTotal = payups.reduce((sum, p) => sum + Number(p.amount || 0), 0);
  const hasPayup = payups.length > 0;

  // 配送時間：標籤（立即配送／預約配送）+ 日期；預約配送另顯示完整時段（09:00–12:00，待後端帶入）。
  // 註：delivery_time_stamp 目前只是「日期 + 中午」的錨點，非真正時段時間，故立即配送只取日期、預約時段以 slot label 為準。
  const deliveryOrderList = snapshot?.order_list;
  const isImmediateDelivery =
    (deliveryOrderList?.delivery_type || "").includes("立即") || deliveryOrderList?.delivery_type === "immediate";
  const deliveryLabel = isImmediateDelivery ? "立即配送" : "預約配送";
  const deliveryDateText = deliveryOrderList?.delivery_time_stamp
    ? (() => {
        try {
          return format(new Date(deliveryOrderList.delivery_time_stamp), "yyyy/MM/dd");
        } catch {
          return "";
        }
      })()
    : "";
  // 後端 snapshot 接上配送時段 module 後，order_list 會帶完整時段字串（例「09:00–12:00」）
  const deliverySlotLabel = deliveryOrderList?.delivery_slot_label || "";
  const deliveryWhenText = isImmediateDelivery
    ? deliveryDateText
    : [deliveryDateText, deliverySlotLabel].filter(Boolean).join("  ");

  // 與「Apple Pay」等付款方式文字同一列，避免 tag 對齊到「支付方式」標題而視覺跑版。
  const paymentStatusBadge =
    payStatus &&
    (payStatus.payment_status === "cash_fallback" ? (
      <View br={999} px={10} py={3} bg={colors.bgGreen} flexShrink={0}>
        <Text fontSize={12} fontWeight="800" color={colors.statusCompleted}>
          已付款（現金）
        </Text>
      </View>
    ) : payStatus.paid ? (
      <View br={999} px={10} py={3} bg={colors.bgGreen} flexShrink={0}>
        <Text fontSize={12} fontWeight="800" color={colors.statusCompleted}>
          已付款
        </Text>
      </View>
    ) : payStatus.payable ? (
      <XStack
        ai="center"
        br={999}
        px={14}
        py={6}
        flexShrink={0}
        className="primary-bg-gradient"
        cursor="pointer"
        pressStyle={{ opacity: 0.85 }}
        onPress={() => navigate(`/pay/${orderId}`)}>
        <Text fontSize={12} fontWeight="800" color={colors.light}>
          前往付款
        </Text>
      </XStack>
    ) : (
      <View br={999} px={10} py={3} bg={colors.bgAmber} flexShrink={0}>
        <Text fontSize={12} fontWeight="800" color={colors.statusPending}>
          未付款
        </Text>
      </View>
    ));

  // 卡片依序上浮（見 all.scss 的 .gas-stagger-kids）。掛在清單容器這一層而不是 PageWrapper：
  // /orders* 在那邊被排除，因為卡片都包在各頁自己的容器裡，從外層數只會數到一個。
  return (
    <ScrollView>
      <YStack className="gas-stagger-kids" gap={skinV2 ? 12 : "$3"} pb="$5">
        <DCard space="$4">
          <XStack jc="space-between" ai="center" gap="$2">
            {refundHeader ? (
              <RefundStatusBadge label={refundHeader.label} done={refundHeader.done} />
            ) : (
              <StatusBadge status={uiStatus} />
            )}
            <YStack ai="flex-end" flexShrink={0}>
              <Text fontSize={12} color={colors.gray200}>
                建立時間
              </Text>
              <Text fontSize={13} fow="600">
                {order.create_time_stamp ? format(new Date(order.create_time_stamp), "yyyy/MM/dd HH:mm") : "-"}
              </Text>
            </YStack>
          </XStack>
          {/* 有退款：不顯示待確認～已完成 timeline，改由下方退款進度卡承接 */}
          {!showRefundUi && <StatusBar status={uiStatus} />}
        </DCard>

        {shouldShowPaymentPin({ paymentPin, uiStatus }) && paymentPin ? <PaymentPinCard pin={paymentPin} /> : null}

        {hasModifications(order.modifications) && <ModificationCard list={order.modifications!} />}

        {showRefundUi && <RefundCard refunds={refundOrders} orderTotal={payableTotal} />}

        <DCard space="$3">
          <DTitle label="配送資訊" />
          <YStack space={skinV2 ? undefined : "$3"} gap={skinV2 ? 16 : undefined}>
            <InfoRow icon={<RiMapPinLine size={16} color={colors.primary} />} label="配送地址">
                <Text fontSize={14} fow="600">
                {order.address_snapshot_json?.full_address}
              </Text>
            </InfoRow>
            <InfoRow icon={<RiTimeLine size={16} color={colors.primary} />} label="配送時間">
                <Text fontSize={14} fow="600">
                {deliveryLabel}
              </Text>
              {deliveryWhenText ? (
                <Text fontSize={12} color={colors.text} mt={1}>
                  {deliveryWhenText}
                </Text>
              ) : null}
            </InfoRow>
            <InfoRow icon={<RiPhoneLine size={16} color={colors.primary} />} label="聯絡電話">
                <Text fontSize={14} fow="600">
                {order.supplier_order_snapshot_json?.order_list?.customer_phone || "-"}
              </Text>
            </InfoRow>
            <InfoRow icon={<RiCoinsLine size={16} color={colors.primary} />} label="支付方式">
              {/* 線上付款狀態（信用卡快照才查）：放在永遠可見的支付方式列，
                  不放金額區——hide_amount_on_pending 開啟時金額區整塊隱藏會一起被藏掉 */}
              <XStack ai="center" jc="space-between" gap="$2">
                <Text fontSize={14} fow="600" f={1} flexShrink={1}>
                  {paymentMethodLabel}
                </Text>
                {paymentStatusBadge}
              </XStack>
            </InfoRow>
          </YStack>
        </DCard>

        <DCard space="$3">
          <DTitle label="訂購項目" />
          {order.supplier_order_snapshot_json?.items?.map((it, idx) => {
            // 瓦斯：不顯示液化氣／丙烷字樣；商品（其他訂單品項）：顯示品名
            const title = it.kind === "gas" ? formatGasTitle(it.gas_type, it.gas_kilogram ?? "") : it.commodity_name ?? "商品";
            const qty = it.kind === "gas" ? it.numbers_of_cylinder : it.numbers_of_commodity;

            return (
              <XStack key={`item-${idx}`} gap="$3" ai="center" my={skinV2 ? 0 : "$2"}>
                <YStack width={skinV2 ? 48 : 56} height={skinV2 ? 48 : 56} ai="center" jc="center" flexShrink={0}>
                  {it.kind === "gas" ? (
                    <img src={getGasImageByKg(it.gas_kilogram ?? "")} alt="Gas" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                  ) : (
                    <RiFileList3Line size={24} color={colors.gray} />
                  )}
                </YStack>

                <YStack f={1}>
                  <Text fontSize={14} fow="600" color={skinV2 ? v2.title : undefined}>
                    {title} × {qty}
                  </Text>
                  {it.delivery_location ? (
                    <XStack gap="$2" mt="$1">
                      <View bg={skinV2 ? v2.chipBg : colors.bg} px={skinV2 ? 8 : "$2"} py={skinV2 ? 3 : "$0.5"} br={skinV2 ? 8 : 4}>
                        <Text fontSize={11} color={skinV2 ? v2.body : colors.text}>
                          {it.delivery_location}
                        </Text>
                      </View>
                    </XStack>
                  ) : null}
                </YStack>

                {!hideAmount &&
                  (() => {
                    const original = Number(it.subtotal || 0);
                    const shown = Math.max(0, original - (gasItemDiscount.get(idx) ?? 0));
                    const hasDiscount = shown < original;
                    return (
                      <YStack ai="flex-end" flexShrink={0}>
                        <Text fontSize={16} fow="700" color={colors.primary}>
                          NT$ {shown.toLocaleString()}
                        </Text>
                        {hasDiscount && (
                          <Text fontSize={12} color={colors.gray} style={{ textDecorationLine: "line-through" }}>
                            NT$ {original.toLocaleString()}
                          </Text>
                        )}
                      </YStack>
                    );
                  })()}
              </XStack>
            );
          })}
        </DCard>

        {!hideAmount && (
        <DCard space="$2">
          <DTitle label="訂單摘要" />
          <YStack gap={8}>
            {/* 品項：瓦斯／商品 + 鋼瓶買賣 + 押退桶 */}
            <YStack gap={4}>
              {orderItems.map((it, idx) => {
                const name = it.kind === "gas" ? formatGasTitle(it.gas_type, it.gas_kilogram ?? "") : it.commodity_name ?? "商品";
                const qty = it.kind === "gas" ? it.numbers_of_cylinder : it.numbers_of_commodity;
                // 瓦斯品項：折抵優惠券後的顯示價（與訂購頁／確認頁一致；clamp ≥ 0 防呆）
                const original = Number(it.subtotal || 0);
                const shown = Math.max(0, original - (gasItemDiscount.get(idx) ?? 0));
                const hasDiscount = shown < original;
                return (
                  <XStack key={`item-${idx}`} ai="center" jc="space-between" gap={10}>
                    <Text fontSize={14} color={colors.text} flexShrink={1}>
                      {name} × {qty}
                    </Text>
                    <XStack ai="center" gap={7} flexShrink={0}>
                      {hasDiscount && (
                        <Text fontSize={13} color={colors.gray} style={{ textDecorationLine: "line-through" }}>
                          NT$ {original.toLocaleString()}
                        </Text>
                      )}
                      <Text fontSize={15} fontWeight="bold" color={colors.text}>
                        NT$ {shown.toLocaleString()}
                      </Text>
                    </XStack>
                  </XStack>
                );
              })}
              {/* 鋼瓶買賣（客戶購入 + / 客戶賣出 −） */}
              {cylinders.map((c, i) => {
                const isSell = c.customer_action_type === "客戶賣出";
                const spec = c.cylinder_specification ? `${c.cylinder_specification}KG` : "鋼瓶";
                return (
                  <TextSpaceBetween
                    key={`cyl-${i}`}
                    title={`鋼瓶 ${spec}（${isSell ? "賣出" : "購入"}）× ${c.numbers_of_cylinder}`}
                    price={`${isSell ? "-" : ""}NT$ ${Number(c.subtotal).toLocaleString()}`}
                  />
                );
              })}
              {/* 押退桶（押桶／借桶／還桶／寄桶，依 take_cylinder_type 分列） */}
              {mortgages.map((m, i) => {
                const spec = m.cylinder_specification ? ` ${m.cylinder_specification}KG` : "";
                return (
                  <TextSpaceBetween
                    key={`mortgage-${i}`}
                    title={`${m.take_cylinder_type ?? "押退桶"}${spec} × ${m.numbers_of_cylinder}`}
                    price={`NT$ ${Number(m.subtotal).toLocaleString()}`}
                  />
                );
              })}
            </YStack>
            {/* 額外費用 / 折抵 */}
            {usageFeeTotal > 0 || refunds.length > 0 || discountTotal > 0 || floorFee > 0 ? (
              <>
                <hr />
                <YStack gap={4}>
                  {usageFeeTotal > 0 ? <TextSpaceBetween title="使用費" price={`NT$ ${usageFeeTotal.toLocaleString()}`} /> : null}
                  {floorFee > 0 ? <TextSpaceBetween title="樓層費" price={`NT$ ${floorFee.toLocaleString()}`} /> : null}
                  {refunds.map((r, i) => (
                    <TextSpaceBetween
                      key={`refund-${i}`}
                      title={r.order_refund_type === "存氣" ? "存氣（下次折抵）" : "退氣"}
                      price={`-NT$ ${Number(r.amount).toLocaleString()}`}
                    />
                  ))}
                  {floorFeeDiscount > 0 ? <TextSpaceBetween title="樓層費折抵" price={`-NT$ ${floorFeeDiscount.toLocaleString()}`} /> : null}
                  {Number(snapshot?.order_list?.gas_discount || 0) > 0 ? (
                    <TextSpaceBetween title="瓦斯折扣" price={`-NT$ ${Number(snapshot?.order_list?.gas_discount).toLocaleString()}`} />
                  ) : null}
                  {Number(snapshot?.order_list?.discount || 0) > 0 ? (
                    <TextSpaceBetween title="折扣" price={`-NT$ ${Number(snapshot?.order_list?.discount).toLocaleString()}`} />
                  ) : null}
                  {/* 優惠券不單列：已折在上方瓦斯品項價格上（與訂購頁一致） */}
                </YStack>
              </>
            ) : null}
            <hr />
            <YStack gap={4}>
              <XStack alignItems="flex-start" justifyContent="space-between" mb={4}>
                <Text fontSize={16} fontWeight="800">
                  應付金額
                </Text>
                <YStack ai="flex-end" gap={3}>
                  <Text fontSize={22} color={colors.primary} fontWeight="bold">
                    NT$ {payableTotal.toLocaleString()}
                  </Text>
                  {couponDiscount > 0 && (
                    <XStack ai="center" gap={4}>
                      <RiCoupon3Line color={colors.primary} size={13} />
                      <Text fontSize={12} fontWeight="bold" color={colors.primary}>
                        已套用優惠券 −NT$ {couponDiscount.toLocaleString()}
                      </Text>
                    </XStack>
                  )}
                </YStack>
              </XStack>
              {/* 已完成訂單：呈現實際付款方式與實付金額 */}
              {uiStatus === "completed" && hasPayup && (
                <>
                  <XStack alignItems="center" justifyContent="space-between">
                    <Text fontSize={14} color={colors.text}>
                      付款方式
                    </Text>
                    <Text fontSize={14} fontWeight="700" color={colors.text}>
                      {paymentMethodLabel}
                    </Text>
                  </XStack>
                  <XStack alignItems="center" justifyContent="space-between">
                    <Text fontSize={14} fontWeight="700" color={colors.text}>
                      實付金額
                    </Text>
                    <Text fontSize={18} fontWeight="800" color={colors.statusCompleted}>
                      NT$ {paidTotal.toLocaleString()}
                    </Text>
                  </XStack>
                </>
              )}
            </YStack>
          </YStack>
        </DCard>
        )}
        {/* 瓦斯行：交易對象是瓦斯行（平台只做媒合／代收），取消與退換都要直接找他們，
            故名稱、統編、電話要在訂單上查得到。統編／電話由 bootstrap 補齊，缺漏則各自省略。 */}
        {(supplierContact?.name || order.supplier?.supplier_name) && (
          <DCard space="$3">
            <DTitle label="瓦斯行" />
            <YStack space={skinV2 ? undefined : "$3"} gap={skinV2 ? 16 : undefined}>
              <InfoRow icon={<RiStore2Line size={16} color={colors.primary} />} label="店家名稱">
                <Text fontSize={14} fow="600">
                  {supplierContact?.name || order.supplier?.supplier_name}
                </Text>
                {supplierContact?.taxId && (
                  <Text fontSize={12} color={colors.gray} mt={1}>
                    統編 {supplierContact.taxId}
                  </Text>
                )}
              </InfoRow>
              {supplierContact?.phone && (
                <InfoRow icon={<RiPhoneLine size={16} color={colors.primary} />} label="瓦斯行電話">
                  {/* 取消或退換須直接聯繫瓦斯行 → 可直接撥號 */}
                  <Text
                    fontSize={14}
                    fow="600"
                    color={colors.primary}
                    cursor="pointer"
                    pressStyle={{ opacity: 0.6 }}
                    onPress={() => window.open(toTelHref(supplierContact.phone as string))}>
                    {supplierContact.phone}
                  </Text>
                </InfoRow>
              )}
            </YStack>
          </DCard>
        )}

        {/* {uiStatus !== "completed" && (
          <Button bg={colors.light} borderWidth={1} borderColor={colors.gray100} br={12} h={48} onPress={() => {}}>
            <Text fow="600">聯絡瓦斯行</Text>
          </Button>
        )} */}
      </YStack>
    </ScrollView>
  );
};

// 補上遺漏的 icon
const RiFileList3Line = ({ size, color }: { size: number; color: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

export default OrderDetail;
